import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type {
    ChangeEventHandler,
    FunctionComponent,
    KeyboardEvent,
    MouseEvent,
    MouseEventHandler,
    SetStateAction,
} from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { BaseSkill, SkillShort } from '~/shared/data-contract/skill';
import { IconCrossSmall, IconSearch } from '~/ui/core/icons';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useNavigationToEntity } from '../common/use-navigation-to-entity';
import { useManySkillsQuery } from '../query-hooks/skills';
import type { DropdownData } from './DropDown';
import { buildJobLevelSearchResultUrl, buildJobRoleSearchResultUrl } from './job-architecture-result-url';

type GlobalSearchInputV2Props = {
    readonly addSkillToSearch: (skillId: string) => void;
    readonly currentCursor: number;
    readonly dropDownData: readonly DropdownData[];
    readonly getActiveDropdownElement: () => DropdownData | undefined;
    readonly handleGlobalSearch: () => void;
    readonly onCloseDropdown: () => void;
    readonly onOpenDropdown: () => void;
    readonly searchTerm: string;
    readonly selectedSkills: readonly BaseSkill[];
    readonly setCurrentCursor: (value: SetStateAction<number>) => void;
    readonly setSearchTerm: (value: SetStateAction<string>) => void;
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
};

type GlobalSearchSkillChipProps = {
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
    readonly skills: readonly SkillShort[];
};

const SkillChipsContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyContent: 'flex-start',
    paddingRight: theme.spacing.m,
}));

const GlobalSearchSkillChip: FunctionComponent<GlobalSearchSkillChipProps> = ({ setSkillIds, skillIds, skills }) => {
    const { actions } = useContext(ApplicationContext);

    const handleDelete = (skillId: string): void => {
        const updatedSkillIds = skillIds.filter((id) => id !== skillId);

        setSkillIds(updatedSkillIds);
    };

    return (
        <SkillChipsContainer aria-label="Global Search Skill Chips">
            {skills.map((skill) => {
                return (
                    <SkillBadge
                        key={skill.id}
                        actions={
                            <IconCrossSmall
                                color={theme.colors.gray[700]}
                                size={16}
                                onClick={(e: MouseEvent<SVGSVGElement>) => {
                                    e.stopPropagation();
                                    handleDelete(skill.id);
                                }}
                            />
                        }
                        className="h-[var(--spacing-7xl)]"
                        skill={{ title: skill.title, type: skill.skillType }}
                        onClick={() => actions.openSkillDetails(skill.id)}
                    />
                );
            })}
        </SkillChipsContainer>
    );
};

type ExactSkillsSectionProps = {
    readonly selectedSkills: readonly BaseSkill[];
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
};

const ExactSkillsSection: FunctionComponent<ExactSkillsSectionProps> = ({ selectedSkills, setSkillIds, skillIds }) => {
    const { data } = useManySkillsQuery({ enabled: is.nonEmptyArray(skillIds), skillIds });
    const skillsById = new Map([...selectedSkills, ...(data ?? [])].map((skill) => [skill.id, skill]));
    const skills = skillIds.flatMap((skillId) => {
        const skill = skillsById.get(skillId);

        return skill === undefined ? [] : [skill];
    });

    if (is.emptyArray(skills)) {
        return <span />;
    }

    return <GlobalSearchSkillChip setSkillIds={setSkillIds} skillIds={skillIds} skills={skills} />;
};

const SearchInputContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors.gray[300],
    borderRadius: '9999px',
    boxShadow: 'var(--shadow-recessed-pocket-neutral)',
    boxSizing: 'border-box',
    display: 'flex',
    height: theme.spacing['10xl'],
    minHeight: theme.spacing['10xl'],
    padding: `${theme.spacing.xs} ${theme.spacing.l} ${theme.spacing.xs} ${theme.spacing.l}`,
}));

const InputWrapper = styled.div({
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: theme.spacing.s,
    minWidth: 0,
});

const StyledInput = styled.input(({ theme }) => ({
    '&::placeholder': {
        color: theme.colors.gray[700],
    },
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.black[900],
    flex: 1,
    ...theme.typography.body.S.regular,
    minWidth: '120px',
    outline: 'none',
    padding: 0,
    width: '100%',
}));

const SearchButton = styled.button(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.gray[900],
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    height: '24px',
    justifyContent: 'center',
    padding: 0,
    width: '24px',
}));

export const GlobalSearchInputV2: FunctionComponent<GlobalSearchInputV2Props> = ({
    addSkillToSearch,
    dropDownData,
    getActiveDropdownElement,
    handleGlobalSearch,
    onCloseDropdown,
    onOpenDropdown,
    searchTerm,
    selectedSkills,
    setCurrentCursor,
    setSearchTerm,
    setSkillIds,
    skillIds,
}) => {
    const { navigateToJobPage, navigateToLearningOpportunityPage, navigateToUserProfile } = useNavigationToEntity();
    const { actions, translate } = useContext(ApplicationContext);

    const onDeleteLastSkill = (): void => {
        const updatedSkillIds = skillIds.slice(0, -1);
        setSkillIds(updatedSkillIds);
    };

    const changeSearchTerm: ChangeEventHandler<HTMLInputElement> = (event): void => {
        const searchValue = event.target.value;
        onOpenDropdown();
        setSearchTerm(searchValue);
    };

    const handleClick: MouseEventHandler = (event) => {
        event.stopPropagation();
        handleGlobalSearch();
    };

    const handleEnterKey = (): void => {
        const active = getActiveDropdownElement();

        if (is.undefined(active)) {
            handleGlobalSearch();

            return;
        }

        const { id, type } = active;
        if (type === 'job') {
            navigateToJobPage(id);
        }
        if (type === 'skill') {
            addSkillToSearch(id);
        }
        if (type === 'user') {
            navigateToUserProfile(id);
        }
        if (type === 'learning-opportunity') {
            navigateToLearningOpportunityPage(id);
        }
        if (type === 'job-role') {
            actions.navigateTo(buildJobRoleSearchResultUrl(active));
        }
        if (type === 'job-level') {
            actions.navigateTo(buildJobLevelSearchResultUrl(active));
        }

        onCloseDropdown();
        setSearchTerm('');
        setCurrentCursor(-1);
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
        e.stopPropagation();

        switch (e.key) {
            case 'Enter':
                handleEnterKey();
                break;
            case 'ArrowUp':
                setCurrentCursor((prevState) => (prevState > 0 ? prevState - 1 : -1));
                break;
            case 'ArrowDown':
                setCurrentCursor((prevState) => (prevState === dropDownData.length - 1 ? 0 : prevState + 1));
                break;
            case 'Backspace':
                if (is.emptyString(searchTerm)) {
                    onDeleteLastSkill();
                }
                break;
            default:
                break;
        }
    };

    return (
        <SearchInputContainer aria-label="Global Search Input">
            <InputWrapper>
                <SearchButton id="global-search-button" type="button" onClick={handleClick}>
                    <IconSearch size={16} />
                </SearchButton>
                <ExactSkillsSection selectedSkills={selectedSkills} setSkillIds={setSkillIds} skillIds={skillIds} />
                <StyledInput
                    aria-label="Global Search Input Field"
                    autoComplete="off"
                    id="global-search-input"
                    placeholder={translate('pages.global-search.placeholder')}
                    type="text"
                    value={searchTerm}
                    onChange={changeSearchTerm}
                    onClick={onOpenDropdown}
                    onKeyDown={handleKeyPress}
                />
            </InputWrapper>
        </SearchInputContainer>
    );
};
